import {
  makeContentServiceGetRequest,
  makeContentServicePostRequest,
} from 'infrastructure/contentService'
import type {
  BatchStatusResponse,
  BatchRegisterParams,
  BatchRegisterResponse,
  BatchDeregisterParams,
  BatchDeregisterResponse,
} from './types'

const BATCH_BASE_PATH = 'content/v1/ai-tree-tool/batch'

/**
 * Get batch status: state and registered jobs.
 *
 * @returns Promise<BatchStatusResponse> - Batch state and jobs list
 */
export async function getBatchStatus(): Promise<BatchStatusResponse> {
  const response = await makeContentServiceGetRequest(
    `${BATCH_BASE_PATH}/status`,
  )

  const rawResponse = await response.text()
  if (!response.ok) {
    const error = new Error(rawResponse) as Error & { status?: number }
    error.status = response.status
    throw error
  }

  try {
    return JSON.parse(rawResponse)
  } catch {
    throw new Error(rawResponse)
  }
}

/**
 * Register a tree in the batch.
 *
 * @param params - tree_id, tree_ver, area, prompt_id (required), title (optional, default "")
 * @returns Promise<BatchRegisterResponse>
 */
export async function registerBatch(
  params: BatchRegisterParams,
): Promise<BatchRegisterResponse> {
  const response = await makeContentServicePostRequest(
    `${BATCH_BASE_PATH}/register`,
    params,
  )

  const rawResponse = await response.text()
  if (!response.ok) {
    const error = new Error(rawResponse) as Error & { status?: number }
    error.status = response.status
    throw error
  }

  try {
    return JSON.parse(rawResponse)
  } catch {
    throw new Error(rawResponse)
  }
}

/**
 * Deregister a tree from the batch.
 *
 * @param params - tree_id, tree_ver (both required)
 * @returns Promise<BatchDeregisterResponse>
 */
export async function deregisterBatch(
  params: BatchDeregisterParams,
): Promise<BatchDeregisterResponse> {
  const response = await makeContentServicePostRequest(
    `${BATCH_BASE_PATH}/deregister`,
    params,
  )

  const rawResponse = await response.text()
  if (!response.ok) {
    const error = new Error(rawResponse) as Error & { status?: number }
    error.status = response.status
    throw error
  }

  try {
    return JSON.parse(rawResponse)
  } catch {
    throw new Error(rawResponse)
  }
}
